import type { OperateWizardState, WizardDestination, WizardInflow, WizardSource } from './types';

/**
 * Compute the inflow set a newly-added destination should start with given
 * the current mode. Split / split-and-merge always pull from the initial
 * source; merge pulls from every listed source; redistribution starts empty
 * so the operator picks explicitly.
 */
export function autoInflowsForMode(state: OperateWizardState): WizardInflow[] {
    switch (state.operationKind) {
        case 'split':
        case 'split_and_merge':
            return [{ sourceFishGroupId: state.initialSourceId, estimatedFishCount: '' }];
        case 'merge':
            return state.sources.map((s) => ({
                sourceFishGroupId: s.fishGroupId,
                estimatedFishCount: '',
            }));
        default:
            return [];
    }
}

/**
 * Apply a merge-target selection for the split-and-merge flow. Shared between
 * the occupied and empty branches: both rewrite destinations[1], strip any
 * previously-attached second source, and toggle `samTargetMode`. The only
 * difference is whether a second source is attached eagerly (occupied →
 * occupant becomes source 2) or deferred to the explicit picker step
 * (empty → second source added later via `setSecondSource`).
 */
export function applyTargetSelection(
    state: OperateWizardState,
    args: {
        mode: 'occupied' | 'empty';
        cageId: number;
        secondSource: WizardSource | null;
    }
): OperateWizardState {
    const { mode, cageId, secondSource } = args;
    const prevTarget = state.destinations[1];
    const sourcesWithoutSecond = state.sources.filter(
        (s) => s.fishGroupId === state.initialSourceId
    );
    const sources = secondSource ? [...sourcesWithoutSecond, secondSource] : sourcesWithoutSecond;

    const receivesFrom: WizardInflow[] = [
        { sourceFishGroupId: state.initialSourceId, estimatedFishCount: '' },
    ];
    if (secondSource) {
        receivesFrom.push({
            sourceFishGroupId: secondSource.fishGroupId,
            estimatedFishCount: '',
        });
    }

    // The estimated count is context-bound: changing the cage or the branch
    // invalidates whatever was there (either an earlier prefill or a value
    // tied to a different occupant). Name and notes are identity-bound to
    // the operator's intent and survive across cage swaps.
    const preserveCount =
        prevTarget != null && prevTarget.cageId === cageId && state.samTargetMode === mode;

    const newTarget: WizardDestination = {
        tempId: prevTarget?.tempId ?? generateTempId(),
        name: prevTarget?.name ?? '',
        cageId,
        estimatedFishCount: preserveCount ? prevTarget.estimatedFishCount : '',
        receivesFrom,
        notes: prevTarget?.notes ?? '',
    };

    const continuation = state.destinations[0];
    const destinations: WizardDestination[] = continuation
        ? [continuation, newTarget]
        : [newTarget];

    return pruneOrphanedBioscopeMovements({
        ...state,
        sources,
        destinations,
        samTargetMode: mode,
    });
}

/**
 * Drop any bioscope movement whose `targetCageId` no longer matches a
 * declared destination cage. Called after every action that mutates the
 * destination list (removal, cage-id change, target replacement) so the
 * `bioscope_movements` step's decisions can't outlive the destination they
 * referenced — otherwise the request payload would ship a bioscope to a
 * cage that is no longer part of the operation. Idempotent: returns the
 * same state reference when nothing is pruned.
 */
export function pruneOrphanedBioscopeMovements(state: OperateWizardState): OperateWizardState {
    if (state.bioscopeMovements.length === 0) return state;
    const declaredCageIds = new Set(
        state.destinations.map((d) => d.cageId).filter((id): id is number => id != null)
    );
    const filtered = state.bioscopeMovements.filter((m) => declaredCageIds.has(m.targetCageId));
    if (filtered.length === state.bioscopeMovements.length) return state;
    return { ...state, bioscopeMovements: filtered };
}

export function mapDestination(
    state: OperateWizardState,
    tempId: string,
    fn: (d: WizardDestination) => WizardDestination
): OperateWizardState {
    return {
        ...state,
        destinations: state.destinations.map((d) => (d.tempId === tempId ? fn(d) : d)),
    };
}

/**
 * Generate a temp id for a destination. Uses `crypto.randomUUID` when
 * available (browsers + Node 16+), falls back to a randomised string for
 * older environments / SSR edge cases.
 */
export function generateTempId(): string {
    if (typeof globalThis.crypto !== 'undefined' && globalThis.crypto.randomUUID) {
        return globalThis.crypto.randomUUID();
    }
    return `dest-${Math.random().toString(36).slice(2, 11)}-${Date.now()}`;
}
